// context/ServiceContext.js
import React, { createContext, useContext, useMemo } from 'react';
import AuthService from '../service/AuthService';
import IntroductionService from '../service/IntroductionService';
import BlacklistService from '../service/BlacklistService';
import ContactService from '../service/ContactService';
import FeedbackService from '../service/FeedbackService';
import UserDataService from '../service/UserDataService';
import { addAuthInterceptor } from '../service/AuthInterceptor';
import TemplateService from '../service/TemplateService';

// 1. Define the TypeScript Interface for your services object
interface AppServices {
  contactService: ContactService;
  analyticsService: IntroductionService;
  authService: AuthService;
  feedbackService: FeedbackService;
  blacklistService: BlacklistService;
  userDataService: UserDataService;
}

// Create a single context that holds an object of all initialized services
const ServiceContext = createContext<AppServices | null>(null);

const API_URL = "/"

/**
 * Custom hook to access all services at once.
 */
export const useServices = () => {
  const services = useContext(ServiceContext);
  if (!services) {
    throw new Error('useServices must be used within a ServiceProvider');
  }
  return services;
};

/**
 * Provider component that initializes all services using configuration from exports.json.
 */
export const ServiceProvider = ({ children, context }) => {
  const authService = new AuthService(
    API_URL
  );

  const authInterceptor = addAuthInterceptor(authService);
  const services = useMemo(() => {

    return {
      contactService: new ContactService(API_URL, authInterceptor),
      analyticsService: new IntroductionService(API_URL, authInterceptor),
      authService: authService, // This one remains unchanged as it has one or zero parameters
      feedbackService: new FeedbackService(API_URL),
      blacklistService: new BlacklistService(API_URL),
      userDataService: new UserDataService(API_URL, authInterceptor),
      introductionService: new IntroductionService(API_URL, authInterceptor),
      templateService: new TemplateService(API_URL, authInterceptor)
    };
  }, [API_URL]); // Dependency array for memoization

  return (
    <ServiceContext.Provider value={services}>
      {children}
    </ServiceContext.Provider>
  );
};